/**
 * Local version history for the Mac app.
 *
 * A snapshot is taken when a save succeeds, throttled so an afternoon of
 * autosaves does not produce hundreds of near-identical rows: a save inside
 * the coalescing window refreshes nothing — the previous snapshot already
 * tells the story of that stretch of writing. The result reads like a
 * timeline of sittings rather than a keystroke log.
 *
 * The website never imports this module.
 */

import {
  insertSnapshot,
  latestSnapshot,
  listSnapshots,
  pruneSnapshots,
  type SnapshotRow,
} from "@/lib/desktop-store";

export type { SnapshotRow };

/** Saves closer together than this fold into the previous snapshot. */
const COALESCE_MS = 5 * 60_000;
/** Per-document cap; pruned oldest-first once exceeded. */
const KEEP = 200;

export async function recordSnapshot(input: {
  documentId: number;
  title: string;
  content: string;
  version: number | null;
  wordCount: number;
}): Promise<void> {
  const latest = await latestSnapshot(input.documentId);
  if (latest && latest.content === input.content) return;
  if (latest && Date.now() - latest.created_at < COALESCE_MS) return;

  await insertSnapshot(input);
  await pruneSnapshots(input.documentId, KEEP);
}

export async function getSnapshots(documentId: number): Promise<SnapshotRow[]> {
  return listSnapshots(documentId, KEEP);
}
